import { createConnectTransport } from '@connectrpc/connect-web'
import { Code, ConnectError, type Interceptor, type Transport } from '@connectrpc/connect'
import { ensureCsrfCookie, readCsrfCookie } from './csrf'

/**
 * Creates the Connect transport the rest of the app consumes via inject().
 *
 * The refreshHook runs exactly once per failing request when the server
 * returns Unauthenticated. It POSTs /auth/refresh (which rotates the opaque
 * refresh cookie server-side and mints a fresh access JWT cookie) and, on
 * success, lets the caller retry transparently. A second failure bubbles up
 * so the router guard can redirect to /auth/login.
 */
export interface CreateTransportOptions {
  baseUrl: string
  refreshHook: () => Promise<boolean>
}

// createAuthInterceptor wraps a refresh-on-unauth flow around any
// unary/streaming request. Exported so tests can drive it without
// pulling in the full Connect transport machinery.
export function createAuthInterceptor(refreshHook: () => Promise<boolean>): Interceptor {
  return (next) => async (req) => {
    try {
      return await next(req)
    } catch (err) {
      if (!isUnauthenticated(err)) throw err
      const refreshed = await refreshHook()
      if (!refreshed) throw err
      return next(req)
    }
  }
}

export function createTransport(opts: CreateTransportOptions): Transport {
  const authInterceptor = createAuthInterceptor(opts.refreshHook)

  return createConnectTransport({
    baseUrl: opts.baseUrl,
    fetch: async (input, init) => {
      const headers = new Headers(init?.headers ?? {})
      if (!headers.has('X-CSRF-Token')) {
        const csrf = readCsrfCookie() || (await ensureCsrfCookie())
        headers.set('X-CSRF-Token', csrf)
      }
      return fetch(input, { ...init, headers, credentials: 'include' })
    },
    useBinaryFormat: false,
    interceptors: [authInterceptor],
  })
}

function isUnauthenticated(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unauthenticated
}
